import React from "react";
import { SEO } from "../../seo/SEO";
import styled from "styled-components";
import { useTypedResource } from "../../i18n/languagehandler";

const Section = styled.section`
  max-width: 1200px;
  margin: 24px auto 60px;
  padding: 0 20px;
`;

const Title = styled.h2`
  color: var(--text);
  font-size: 36px;
  margin: 0 0 20px 0;
  line-height: 1.2;
  text-transform: uppercase;
`;

const Subtitle = styled.h3`
  color: var(--primary);
  font-size: 24px;
  margin: 30px 0 16px 0;
`;

const BodyText = styled.p`
  color: var(--text-secondary);
  font-size: 18px;
  line-height: 1.6;
  margin: 0 0 20px 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-top: 30px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  display: flex;
  flex-direction: column;
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CardContent = styled.div`
  padding: 24px;
  text-align: center;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CardTitle = styled.h3`
  color: var(--text);
  font-size: 20px;
  margin: 0 0 12px 0;
  line-height: 1.3;
`;

const CardDescription = styled.p`
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.5;
  margin: 0 0 20px 0;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: auto;
`;

const PrimaryButton = styled.a`
  background: var(--primary);
  color: #fff;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-block;
  &:hover {
    background: var(--primary-dark);
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(55, 90, 127, 0.3);
  }
`;

const LogosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
  margin-top: 30px;
  align-items: center;
  justify-items: center;
`;

const LogoCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
  &:hover {
    transform: translateY(-2px);
  }
`;

const LogoImage = styled.img`
  max-width: 100%;
  height: auto;
  max-height: 60px;
`;

export const Media: React.FC = () => {
  const res = useTypedResource<{
    title: string;
    intro: string;
    podcastsTitle: string;
    podcastsIntro: string;
    mediaLogosTitle: string;
    mediaLogosIntro: string;
    video1Title: string;
    video1Description: string;
    video1Url: string;
    video2Title: string;
    video2Description: string;
    video2Url: string;
    video5Title: string;
    video5Description: string;
    video5Url: string;
    video8Title: string;
    video8Description: string;
    video8Url: string;
    video9Title: string;
    video9Description: string;
    video9Url: string;
    video10Title: string;
    video10Description: string;
    video10Url: string;
    video11Title: string;
    video11Description: string;
    video11Url: string;
    video12Title: string;
    video12Description: string;
    video12Url: string;
    video13Title: string;
    video13Description: string;
    video13Url: string;
    video14Title: string;
    video14Description: string;
    video14Url: string;
    video15Title: string;
    video15Description: string;
    video15Url: string;
    video16Title: string;
    video16Description: string;
    video16Url: string;
    video17Title: string;
    video17Description: string;
    video17Url: string;
    video18Title: string;
    video18Description: string;
    video18Url: string;
    video19Title: string;
    video19Description: string;
    video19Url: string;
    video20Title: string;
    video20Description: string;
    video20Url: string;
    video21Title: string;
    video21Description: string;
    video21Url: string;
    video22Title: string;
    video22Description: string;
    video22Url: string;
    podcast1Title: string;
    podcast1Description: string;
    podcast1Url: string;
    podcast2Title: string;
    podcast2Description: string;
    podcast2Url: string;
    podcast3Title: string;
    podcast3Description: string;
    podcast3Url: string;
    podcast4Title: string;
    podcast4Description: string;
    podcast4Url: string;
    podcast5Title: string;
    podcast5Description: string;
    podcast5Url: string;
    podcast6Title: string;
    podcast6Description: string;
    podcast6Url: string;
    podcast7Title: string;
    podcast7Description: string;
    podcast7Url: string;
    podcast8Title: string;
    podcast8Description: string;
    podcast8Url: string;
    podcast9Title: string;
    podcast9Description: string;
    podcast9Url: string;
    podcast10Title: string;
    podcast10Description: string;
    podcast10Url: string;
    podcast11Title: string;
    podcast11Description: string;
    podcast11Url: string;
    watchButton: string;
    learnMoreButton: string;
  }>("media");

  const videos = [
    {
      title: res.video21Title,
      description: res.video21Description,
      url: res.video21Url,
      image: "/assets/Media/doomscrolling-hjernen.jpg",
    },
    {
      title: res.video22Title,
      description: res.video22Description,
      url: res.video22Url,
      image: "/assets/Media/skaermtid-fokus.jpg",
    },
    {
      title: res.video1Title,
      description: res.video1Description,
      url: res.video1Url,
      image: "/assets/Media/Picture 1.jpg",
    },
    {
      title: res.video2Title,
      description: res.video2Description,
      url: res.video2Url,
      image: "/assets/Media/Picture 2.jpg",
    },
    {
      title: res.video5Title,
      description: res.video5Description,
      url: res.video5Url,
      image: "/assets/Media/Picture 5.jpg",
    },
    {
      title: res.video8Title,
      description: res.video8Description,
      url: res.video8Url,
      image: "/assets/Media/Picture 8.jpg",
    },
    {
      title: res.video9Title,
      description: res.video9Description,
      url: res.video9Url,
      image: "/assets/Media/Picture 9.jpg",
    },
    {
      title: res.video10Title,
      description: res.video10Description,
      url: res.video10Url,
      image: "/assets/Media/Picture 10.jpg",
    },
    {
      title: res.video11Title,
      description: res.video11Description,
      url: res.video11Url,
      image: "/assets/Media/Picture 11.jpg",
    },
    {
      title: res.video12Title,
      description: res.video12Description,
      url: res.video12Url,
      image: "/assets/Media/Picture 12.jpg",
    },
    {
      title: res.video13Title,
      description: res.video13Description,
      url: res.video13Url,
      image: "/assets/Media/Picture 13.jpg",
    },
    {
      title: res.video14Title,
      description: res.video14Description,
      url: res.video14Url,
      image: "/assets/Media/Picture 14.jpg",
    },
    {
      title: res.video15Title,
      description: res.video15Description,
      url: res.video15Url,
      image: "/assets/Media/Picture 15.jpg",
    },
    {
      title: res.video16Title,
      description: res.video16Description,
      url: res.video16Url,
      image: "/assets/Media/Picture 16.jpg",
    },
    {
      title: res.video17Title,
      description: res.video17Description,
      url: res.video17Url,
      image: "/assets/Media/Picture 17.jpg",
    },
    {
      title: res.video18Title,
      description: res.video18Description,
      url: res.video18Url,
      image: "/assets/Media/Picture 18.jpg",
    },
    {
      title: res.video19Title,
      description: res.video19Description,
      url: res.video19Url,
      image: "/assets/Media/Picture 19.jpg",
    },
    {
      title: res.video20Title,
      description: res.video20Description,
      url: res.video20Url,
      image: "/assets/Media/Picture 20.jpg",
    },
  ];

  const podcasts = [
    {
      title: res.podcast1Title,
      description: res.podcast1Description,
      image: "/assets/Podcasts/Picture 1.jpg",
      url: res.podcast1Url,
    },
    {
      title: res.podcast2Title,
      description: res.podcast2Description,
      image: "/assets/Podcasts/Picture 2 Podcast.jpg",
      url: res.podcast2Url,
    },
    {
      title: res.podcast3Title,
      description: res.podcast3Description,
      image: "/assets/Podcasts/Picture 3 Podcast.jpg",
      url: res.podcast3Url,
    },
    {
      title: res.podcast4Title,
      description: res.podcast4Description,
      image: "/assets/Podcasts/Picture 4_Podcast sybersundhed.jpg",
      url: res.podcast4Url,
    },
    {
      title: res.podcast5Title,
      description: res.podcast5Description,
      image: "/assets/Podcasts/Picture 5_Podcast Signe.jpg",
      url: res.podcast5Url,
    },
    {
      title: res.podcast6Title,
      description: res.podcast6Description,
      image: "/assets/Podcasts/Picture 6_Podcast Brainstorm kopi.jpg",
      url: res.podcast6Url,
    },
    {
      title: res.podcast7Title,
      description: res.podcast7Description,
      image: "/assets/Podcasts/Picture 7_Podcast_D.jpg",
      url: res.podcast7Url,
    },
    {
      title: res.podcast8Title,
      description: res.podcast8Description,
      image: "/assets/Podcasts/Picture 8_Podcast_Psychologos.jpg",
      url: res.podcast8Url,
    },
    {
      title: res.podcast9Title,
      description: res.podcast9Description,
      image: "/assets/Podcasts/Picture 9_WDR.jpg",
      url: res.podcast9Url,
    },
  ];

  const mediaLogos = [
    "/assets/Frontpage/Logoer/DR_logo.svg.png",
    "/assets/Frontpage/Logoer/TV_2_(2023).svg.png",
    "/assets/Frontpage/Logoer/Politiken_newspaper_logo.svg",
    "/assets/Frontpage/Logoer/NDR_Dachmarke.svg.png",
    "/assets/Frontpage/Logoer/WDR_Dachmarke.svg.png",
    "/assets/Frontpage/Logoer/Schweizer_Radio_und_Fernsehen_Logo.svg.png",
    "/assets/Frontpage/Logoer/Skærmbillede 2025-10-16 kl. 12.16.05.png",
  ];

  return (
    <main>
      <SEO title={res.title} description={res.intro} path="/media" />

      <Section>
        <Title>{res.title}</Title>
        <BodyText>{res.mediaLogosIntro}</BodyText>
        <LogosGrid style={{ marginBottom: "3rem" }}>
          {mediaLogos.map((logo, index) => (
            <LogoCard key={index}>
              <LogoImage
                src={encodeURI(process.env.PUBLIC_URL + logo)}
                onError={(e) => {
                  const img = e.currentTarget as HTMLImageElement;
                  img.style.display = "none";
                }}
                alt="Media partner"
              />
            </LogoCard>
          ))}
        </LogosGrid>
        <BodyText>{res.intro}</BodyText>

        <Grid>
          {videos.map((video, index) => (
            <Card key={index}>
              <CardImage
                src={process.env.PUBLIC_URL + video.image}
                alt={video.title}
              />
              <CardContent>
                <CardTitle>{video.title}</CardTitle>
                <CardDescription>{video.description}</CardDescription>
                <ButtonGroup>
                  <PrimaryButton
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {res.watchButton}
                  </PrimaryButton>
                </ButtonGroup>
              </CardContent>
            </Card>
          ))}
        </Grid>

        <Subtitle>{res.podcastsTitle}</Subtitle>
        <BodyText>{res.podcastsIntro}</BodyText>
        <Grid>
          {podcasts.map((podcast, index) => (
            <Card key={index}>
              <CardImage
                src={process.env.PUBLIC_URL + podcast.image}
                alt={podcast.title}
              />
              <CardContent>
                <CardTitle>{podcast.title}</CardTitle>
                <CardDescription>{podcast.description}</CardDescription>
                <ButtonGroup>
                  <PrimaryButton
                    href={podcast.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {res.watchButton}
                  </PrimaryButton>
                </ButtonGroup>
              </CardContent>
            </Card>
          ))}
        </Grid>
      </Section>
    </main>
  );
};
